import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Card, Button, Input } from './ui';
import {
  ensureDiscoveryLinksInitialized,
  genLinkId,
  normalizeLinkUrl,
  type DiscoveryLink,
} from '../lib/discoveryLinks';
import { ChevronDown, ChevronRight, ExternalLink, Plus, X } from 'lucide-react';

// =============================================================================
// AggregatorLinksCard (v0.2.26)
//
// A collapsible card at the top of the Scholarships tab listing scholarship-
// aggregator sites. The list is shared across the household (stored at
// households/{hid}/discovery_links) — every member sees the same links, and
// any member can add or remove them.
//
// Every household starts with the default aggregator sites. New households
// get them seeded at creation; existing households are backfilled via
// ensureDiscoveryLinksInitialized() — once for the original set, and again
// (additively) whenever a later version adds a new default. After that the
// list is fully under the household's control — deleting a default makes it
// stay gone.
//
// Collapsed/expanded state is a per-browser UI preference (localStorage), not
// shared household state.
// =============================================================================

const COLLAPSE_KEY = 'scholarship_app__aggregator_card_collapsed';

function loadCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  } catch {
    // ignore (private browsing / quota)
  }
}

export function AggregatorLinksCard() {
  const { user, membership } = useAuth();
  const [links, setLinks] = useState<DiscoveryLink[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(loadCollapsed());
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Backfill any missing default links (legacy one-time seed + newer
  // versioned defaults), then subscribe.
  useEffect(() => {
    if (!db || !membership || !user) return;
    let unsub: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      try {
        // Seeds the defaults once, then adds any defaults introduced by a
        // later version (no-op when the household is already current). Only a
        // parent may write the household doc, so only a parent runs it —
        // applicants see the result after the next parent load.
        await ensureDiscoveryLinksInitialized(db, membership.householdId, user.uid, membership.role === 'parent');
      } catch (e) {
        // Backfill failure is non-fatal — the live subscription below still
        // shows whatever links exist. Log and continue.
        console.error('[AggregatorLinksCard] link backfill failed:', e);
      }
      if (cancelled || !db) return;
      unsub = onSnapshot(
        collection(db, 'households', membership.householdId, 'discovery_links'),
        (snap) => {
          const list = snap.docs.map((d) => d.data() as DiscoveryLink);
          // Stable alphabetical order so the list doesn't jump around.
          list.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
          setLinks(list);
        },
        (err) => console.error('[AggregatorLinksCard] links snapshot failed:', err)
      );
    })();

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [membership, user]);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      saveCollapsed(next);
      return next;
    });
  }

  async function handleAdd() {
    if (!db || !membership || !user) return;
    setError(null);
    const label = newLabel.trim();
    if (!label) {
      setError('Please enter a name for the link.');
      return;
    }
    const url = normalizeLinkUrl(newUrl);
    if (!url) {
      setError('Please enter a valid web address (e.g. example.com).');
      return;
    }
    setBusy(true);
    try {
      const linkId = genLinkId();
      await setDoc(
        doc(db, 'households', membership.householdId, 'discovery_links', linkId),
        {
          link_id: linkId,
          label,
          url,
          created_by: user.uid,
          created_at: serverTimestamp(),
        }
      );
      setNewLabel('');
      setNewUrl('');
      setAdding(false);
    } catch (e) {
      setError('Could not add link: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(link: DiscoveryLink) {
    if (!db || !membership) return;
    setError(null);
    try {
      await deleteDoc(
        doc(db, 'households', membership.householdId, 'discovery_links', link.link_id)
      );
    } catch (e) {
      setError('Could not remove link: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  return (
    <Card className="p-0 overflow-hidden">
      {/* Header — click to collapse/expand */}
      <button
        type="button"
        onClick={toggleCollapsed}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-cream transition"
      >
        <div className="flex items-center gap-2">
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <span className="font-medium text-ink">Scholarship search sites</span>
          <span className="text-xs text-soft">({links.length})</span>
        </div>
        <span className="text-xs text-soft">{collapsed ? 'Show' : 'Hide'}</span>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 space-y-3 border-t border-line">
          <p className="text-xs text-soft pt-3">
            Quick links to scholarship aggregator sites. Shared with everyone in
            your household — anyone can add or remove links.
          </p>

          {/* Link grid */}
          {links.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {links.map((link) => (
                <div
                  key={link.link_id}
                  className="group flex items-center justify-between gap-2 rounded border border-line px-3 py-2 hover:border-forest-300 hover:bg-forest-50/40 transition"
                >
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-forest-800 hover:underline min-w-0"
                  >
                    <ExternalLink size={13} className="shrink-0" />
                    <span className="truncate">{link.label}</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => handleRemove(link)}
                    className="shrink-0 text-line hover:text-rose-600 transition"
                    title="Remove link"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-soft">
              No links yet. Add one below.
            </p>
          )}

          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              {error}
            </div>
          )}

          {/* Add-link form */}
          {adding ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-cream border border-line rounded p-2">
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Site name"
                className="sm:max-w-[12rem]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                  if (e.key === 'Escape') { setAdding(false); setError(null); }
                }}
                autoFocus
              />
              <Input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="example.com"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                  if (e.key === 'Escape') { setAdding(false); setError(null); }
                }}
              />
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={handleAdd} disabled={busy}>
                  Add
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => { setAdding(false); setError(null); setNewLabel(''); setNewUrl(''); }}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" size="sm" variant="secondary" onClick={() => setAdding(true)}>
              <Plus size={14} className="mr-1" />Add a site
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
